import { useEffect, useState } from 'react';
import { Title } from 'react-admin';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  MenuItem,
  TextField,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  FormControlLabel,
  Switch,
} from '@mui/material';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import SmartToyRoundedIcon from '@mui/icons-material/SmartToyRounded';
import RuleRoundedIcon from '@mui/icons-material/RuleRounded';
import CloudDoneRoundedIcon from '@mui/icons-material/CloudDoneRounded';
import TipsAndUpdatesRoundedIcon from '@mui/icons-material/TipsAndUpdatesRounded';
import ScienceRoundedIcon from '@mui/icons-material/ScienceRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { getSettings, saveSettings, defaultSettings } from '../services/settings';
import { testMeInvoiceConnection } from '../services/meInvoiceClient';
import type { Settings } from '../types';
import { PageHeader } from '../components/PageHeader';
import { CardBody, ContentCard, SectionHeader, pageShellSx } from '../ui';

const MODEL_HINT: Record<string, string> = {
  anthropic: 'Ví dụ: claude-sonnet-5, claude-opus-4-8, claude-haiku-4-5-20251001',
  openai: 'Ví dụ: gpt-4o, gpt-4o-mini',
};

export const SettingsPage = () => {
  const [s, setS] = useState<Settings>(defaultSettings);
  const [saved, setSaved] = useState(false);
  const [meInvoiceTesting, setMeInvoiceTesting] = useState(false);
  const [meInvoiceResult, setMeInvoiceResult] = useState('');
  const [meInvoiceError, setMeInvoiceError] = useState('');

  useEffect(() => {
    getSettings().then(setS);
  }, []);

  const upd = (k: keyof Settings) => (e: any) => setS((p) => ({ ...p, [k]: e.target.value }));
  const updNum = (k: keyof Settings) => (e: any) => setS((p) => ({ ...p, [k]: Number(e.target.value) }));
  const updBool = (k: keyof Settings) => (_: any, checked: boolean) => setS((p) => ({ ...p, [k]: checked }));

  const onProvider = (e: any) => {
    const provider = e.target.value as Settings['provider'];
    setS((p) => {
      let model = p.model;
      if (provider === 'openai' && (!model || model.startsWith('claude'))) model = 'gpt-4o-mini';
      if (provider === 'anthropic' && (!model || model.startsWith('gpt'))) model = 'claude-sonnet-5';
      const authScheme = provider === 'openai' ? 'bearer' : p.authScheme;
      return { ...p, provider, model, authScheme };
    });
  };

  const onSave = async () => {
    await saveSettings(s);
    setSaved(true);
    setTimeout(() => setSaved(false), 2200);
  };

  const testMeInvoice = async () => {
    setMeInvoiceTesting(true); setMeInvoiceResult(''); setMeInvoiceError('');
    try {
      await saveSettings(s);
      const result = await testMeInvoiceConnection();
      setMeInvoiceResult(`Kết nối thành công — đọc được ${result.count} dòng (tổng ${result.total}).`);
    } catch (error: any) {
      setMeInvoiceError(String(error?.message || 'Không thể kết nối MISA meInvoice.'));
    } finally { setMeInvoiceTesting(false); }
  };

  const defaultBase = s.provider === 'openai' ? 'https://api.openai.com' : 'https://api.anthropic.com';
  const providerLabel = s.provider === 'openai' ? 'OpenAI (GPT)' : 'Anthropic (Claude)';

  return (
    <Box sx={pageShellSx}>
      <Title title="Cài đặt" />
      <PageHeader
        title="Cài đặt"
        subtitle="Quản lý cấu hình hệ thống, nhà cung cấp AI và các quy tắc đối chiếu nghiệp vụ."
        icon={<SettingsRoundedIcon />}
      />

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: 'minmax(0, 1fr) 360px' }, gap: 2 }}>
        <Box sx={{ display: 'grid', gap: 2, minWidth: 0 }}>
          <ContentCard>
            <SectionHeader
              icon={<SmartToyRoundedIcon />}
              title="Cấu hình nhà cung cấp AI"
              subtitle="Thiết lập kết nối dùng để bóc tách và phân tích hình ảnh hóa đơn."
              action={<Chip size="small" color="success" icon={<CheckCircleRoundedIcon />} label="Đã cấu hình" />}
            />
            <CardBody>
              <Alert severity="warning" sx={{ mb: 2.2 }}>
                Khóa API được lưu trong bộ nhớ trình duyệt và chỉ phù hợp cho bản thử nghiệm. Khi vận hành thực tế, nên gọi AI qua máy chủ bảo mật.
              </Alert>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                <TextField
                  select
                  fullWidth
                  label="Nhà cung cấp AI"
                  value={s.provider}
                  onChange={onProvider}
                  helperText="Đổi nhà cung cấp sẽ tự gợi ý mô hình và kiểu xác thực phù hợp."
                >
                  <MenuItem value="anthropic">Anthropic (Claude)</MenuItem>
                  <MenuItem value="openai">OpenAI (GPT)</MenuItem>
                </TextField>
                <TextField
                  fullWidth
                  label="Khóa API"
                  type="password"
                  value={s.claudeApiKey}
                  onChange={upd('claudeApiKey')}
                  helperText="Nên sử dụng khóa có quyền truy cập ổn định."
                />
                <TextField
                  fullWidth
                  label={`Mô hình (${providerLabel})`}
                  value={s.model}
                  onChange={upd('model')}
                  helperText={MODEL_HINT[s.provider]}
                />
                <TextField
                  fullWidth
                  label={`URL gốc (để trống = ${defaultBase})`}
                  placeholder={defaultBase}
                  value={s.baseUrl}
                  onChange={upd('baseUrl')}
                  helperText={s.provider === 'openai' ? 'Gọi tới {baseUrl}/v1/chat/completions.' : 'Gọi tới {baseUrl}/v1/messages.'}
                />
                <TextField
                  select
                  fullWidth
                  label="Kiểu xác thực"
                  value={s.authScheme}
                  onChange={upd('authScheme')}
                  disabled={s.provider === 'openai'}
                  helperText={s.provider === 'openai' ? 'OpenAI luôn dùng tiêu đề Authorization: Bearer.' : 'Chọn cơ chế mà máy chủ trung gian Anthropic đang hỗ trợ.'}
                  sx={{ gridColumn: { md: '1 / -1' }, maxWidth: { md: '50%' } }}
                >
                  <MenuItem value="x-api-key">x-api-key (Anthropic chuẩn)</MenuItem>
                  <MenuItem value="bearer">Authorization: Bearer (một số máy chủ trung gian)</MenuItem>
                </TextField>
              </Box>
            </CardBody>
          </ContentCard>

          <ContentCard>
            <SectionHeader
              icon={<RuleRoundedIcon />}
              title="Quy tắc đối chiếu & thiết lập nghiệp vụ"
              subtitle="Thiết lập ngưỡng khớp dữ liệu và thông tin dùng khi tạo lệnh chuyển khoản."
            />
            <CardBody>
              <TextField
                fullWidth
                label="Tên công ty người trả (ghi vào nội dung CK)"
                value={s.payerCompanyName}
                onChange={upd('payerCompanyName')}
                sx={{ mb: 2 }}
              />
              <FormControlLabel
                sx={{ mb: 1.5 }}
                control={<Switch checked={s.automationEnabled} onChange={updBool('automationEnabled')} />}
                label="Tự động xử lý sau khi lưu ảnh đơn nợ"
              />
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' }, gap: 2 }}>
                <TextField
                  fullWidth
                  label="Dung sai % khớp tiền"
                  type="number"
                  value={s.matchAmountTolerancePct}
                  onChange={updNum('matchAmountTolerancePct')}
                  helperText="Sai lệch cho phép theo phần trăm."
                />
                <TextField
                  fullWidth
                  label="Dung sai tuyệt đối (VND)"
                  type="number"
                  value={s.matchAmountToleranceAbs}
                  onChange={updNum('matchAmountToleranceAbs')}
                  helperText="Sai lệch cho phép theo số tiền."
                />
                <TextField
                  fullWidth
                  label="Cửa sổ ngày khớp"
                  type="number"
                  value={s.matchDateWindowDays}
                  onChange={updNum('matchDateWindowDays')}
                  helperText="Khoảng thời gian dùng để đối chiếu."
                />
                <TextField
                  fullWidth
                  label="Ngưỡng confidence bóc tách"
                  type="number"
                  value={s.aiConfidenceThreshold}
                  onChange={updNum('aiConfidenceThreshold')}
                  slotProps={{ htmlInput: { min: 0, max: 1, step: 0.01 } }}
                />
                <TextField
                  fullWidth
                  label="Ngưỡng auto-match"
                  type="number"
                  value={s.autoMatchThreshold}
                  onChange={updNum('autoMatchThreshold')}
                  slotProps={{ htmlInput: { min: 0, max: 1, step: 0.01 } }}
                />
                <TextField
                  fullWidth
                  label="Khoảng cách điểm tối thiểu"
                  type="number"
                  value={s.autoMatchGap}
                  onChange={updNum('autoMatchGap')}
                  slotProps={{ htmlInput: { min: 0, max: 1, step: 0.01 } }}
                />
                <TextField
                  fullWidth
                  label="Ngưỡng AI ánh xạ ngân hàng"
                  type="number"
                  value={s.bankMappingConfidenceThreshold}
                  onChange={updNum('bankMappingConfidenceThreshold')}
                  slotProps={{ htmlInput: { min: 0, max: 1, step: 0.01 } }}
                />
              </Box>
              <Box sx={{ mt: 2.5, display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                <Button variant="contained" onClick={onSave} startIcon={<SaveRoundedIcon />}>Lưu cài đặt</Button>
                {saved && <Alert severity="success" sx={{ py: 0.1 }}>Đã lưu thay đổi</Alert>}
              </Box>
            </CardBody>
          </ContentCard>

          <Accordion disableGutters sx={{ borderRadius: '12px !important', overflow: 'hidden', border: '1px solid', borderColor: 'divider', '&::before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />} sx={{ px: 2.5, minHeight: 68 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                <Box sx={{ width: 38, height: 38, borderRadius: '12px', display: 'grid', placeItems: 'center', bgcolor: 'rgba(14,116,144,.09)', color: 'primary.main' }}><ScienceRoundedIcon /></Box>
                <Box><Typography variant="subtitle1" sx={{ fontWeight: 800 }}>MISA meInvoice</Typography><Typography variant="caption" color="text.secondary">Phiên đăng nhập dành riêng cho app.meinvoice.vn.</Typography></Box>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 2.5, pb: 2.5 }}>
              <Alert severity="warning" sx={{ mb: 2 }}>Thông tin này chứa phiên đăng nhập. Không commit JSON thật vào mã nguồn và cập nhật lại khi phiên hết hạn.</Alert>
              <TextField
                fullWidth label="JSON phiên MISA meInvoice" multiline minRows={7}
                value={s.meInvoiceConfigJson} onChange={upd('meInvoiceConfigJson')}
                placeholder={'{"baseUrl":"https://app.meinvoice.vn","authorization":"Bearer ...","clientId":"...","clientSecret":"...","subscriberCode":"...","subscriberId":"...","organizationId":"...","sessionHeaders":{"ten-header-phien":"..."}}'}
              />
              <Box sx={{ mt: 1.5 }}><Button variant="outlined" onClick={testMeInvoice} disabled={meInvoiceTesting}>{meInvoiceTesting ? 'Đang kiểm tra…' : 'Kiểm tra kết nối meInvoice'}</Button></Box>
              {meInvoiceResult && <Alert severity="success" sx={{ mt: 2 }}>{meInvoiceResult}</Alert>}
              {meInvoiceError && <Alert severity="error" sx={{ mt: 2 }}>{meInvoiceError}</Alert>}
            </AccordionDetails>
          </Accordion>
        </Box>

        <Box sx={{ display: 'grid', alignContent: 'start', gap: 2, minWidth: 0 }}>
          <Card>
            <CardContent sx={{ p: 2.25 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.8 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CloudDoneRoundedIcon color="success" />
                  <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>Trạng thái kết nối</Typography>
                </Box>
                <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: 'success.main', boxShadow: '0 0 0 5px rgba(15,175,120,.10)' }} />
              </Box>
              <Box sx={{ p: 1.7, borderRadius: '12px', bgcolor: 'rgba(15,175,120,.06)', border: '1px solid rgba(15,175,120,.10)' }}>
                <Typography variant="body2" color="success.main" sx={{ fontWeight: 800, mb: 0.8 }}>Cấu hình sẵn sàng</Typography>
                <Typography variant="body2" sx={{ fontWeight: 750 }}>{providerLabel}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.35 }}>URL gốc: {s.baseUrl || defaultBase}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Mô hình: {s.model || '—'}</Typography>
              </Box>
            </CardContent>
          </Card>

          <Card>
            <CardContent sx={{ p: 2.25 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <InfoOutlinedIcon color="primary" />
                <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>Ghi chú & hướng dẫn</Typography>
              </Box>
              <Divider sx={{ mb: 1.5 }} />
              {[
                'Sử dụng khóa API ổn định để tránh gián đoạn quá trình phân tích.',
                'Máy chủ trung gian nội bộ cần tương thích đúng chuẩn của nhà cung cấp và cho phép kết nối chéo nguồn.',
                'Điều chỉnh dung sai theo dữ liệu thực tế của từng doanh nghiệp.',
              ].map((text) => (
                <Box key={text} sx={{ display: 'flex', gap: 1, mb: 1.2 }}>
                  <Box sx={{ width: 6, height: 6, mt: 0.85, borderRadius: '50%', bgcolor: 'primary.main', flexShrink: 0 }} />
                  <Typography variant="body2" color="text.secondary">{text}</Typography>
                </Box>
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardContent sx={{ p: 2.25 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <TipsAndUpdatesRoundedIcon color="primary" />
                <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>Mẹo sử dụng AI hiệu quả</Typography>
              </Box>
              {[
                'Cung cấp ảnh rõ nét, đủ sáng và không mất góc.',
                'Giữ dữ liệu nhà cung cấp và hóa đơn VAT nhất quán.',
                'Kiểm tra kết quả bóc tách trước khi tạo lệnh chi.',
              ].map((text) => (
                <Box key={text} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.25 }}>
                  <CheckCircleRoundedIcon sx={{ fontSize: 18, color: 'primary.main', mt: 0.15 }} />
                  <Typography variant="body2" color="text.secondary">{text}</Typography>
                </Box>
              ))}
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Box>
  );
};
